"use client";
import { z } from "zod";
import { useFormContext } from "react-hook-form";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { REGEXP_ONLY_DIGITS } from "input-otp";
import { ReactNode } from "react";

const emailVerificationFormSchema = z.object({
  pin: z.string().min(6, {
    message: "Your one-time password must be 6 characters.",
  }),
});

export type EmailVerificationFormSchema = z.infer<typeof emailVerificationFormSchema>;
export type EmailVerificationValues = { pin: string };

type EmailVerificationFormProps = {
  id?: string;
  onSuccess: (data: EmailVerificationValues) => void | Promise<void>;
  onError?: () => void | Promise<void>;
  children?: ReactNode;
};

function EmailVerificationForm({ id, onSuccess, onError, children }: EmailVerificationFormProps) {
  const form = useFormContext<EmailVerificationValues>();

  return (
    <Form {...form}>
      <form id={id} onSubmit={form.handleSubmit(onSuccess, onError)}>
        {children}
        <FormField
          control={form.control}
          name="pin"
          render={({ field }) => (
            <FormItem>
              <FormLabel>One-time password</FormLabel>
              <FormControl>
                <InputOTP
                  maxLength={6}
                  autoFocus
                  pattern={REGEXP_ONLY_DIGITS}
                  {...field}
                  className="w-full!"
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                  </InputOTPGroup>
                  <InputOTPGroup>
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </FormControl>

              {!children && (
                <FormDescription className="body-md text-secondary">
                  Verify the 6 digit code sent to your email to continue.
                </FormDescription>
              )}
              <FormMessage />
            </FormItem>
          )}
        />
      </form>
    </Form>
  );
}

export { EmailVerificationForm, emailVerificationFormSchema };
